const SNATCHD_SUBSCRIPTION_DEFAULT_EXPERIMENT = Object.freeze({
  id: "snatchd_subscription_default_v1",
  queryParameter: "snatchd_sub_default",
  cookieName: "snatchd_sub_default_v1",
  cartAttribute: "snatchd_subscription_default_v1",
  active: false,
  allocation: 0.5,
  maxAgeSeconds: 30 * 24 * 60 * 60,
  control: "control",
  treatment: "auto_refill",
});

const SNATCHD_SUBSCRIPTION_INTRO_RENEWAL_PLANS = Object.freeze({
  "3799253044": "3784278068",
  "3799285812": "3784704052",
  "3799318580": "3784736820",
  "3820453940": "3784278068",
  "3820486708": "3784704052",
  "3820519476": "3784736820",
});

const SNATCHD_SUBSCRIPTION_INTRO_DISCOUNTS = Object.freeze({
  "3799253044": 20,
  "3799285812": 20,
  "3799318580": 20,
  "3820453940": 25,
  "3820486708": 25,
  "3820519476": 25,
});

function readSnatchdExperimentCookie(cookieString, cookieName) {
  const encodedName = `${encodeURIComponent(cookieName)}=`;
  const matchingCookie = String(cookieString || "")
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(encodedName));
  if (!matchingCookie) return "";
  try {
    return decodeURIComponent(matchingCookie.slice(encodedName.length));
  } catch (_error) {
    return "";
  }
}

function getSnatchdExperimentRandomValue() {
  try {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return values[0] / 4294967296;
  } catch (_error) {
    return Math.random();
  }
}

function resolveSnatchdSubscriptionDefaultExperiment(options = {}) {
  const config = SNATCHD_SUBSCRIPTION_DEFAULT_EXPERIMENT;
  const search = options.search ?? window.location.search;
  const cookieString = options.cookieString ?? document.cookie;
  const active = options.active ?? config.active;
  const allowedVariants = new Set([config.control, config.treatment]);
  const queryVariant = new URLSearchParams(search).get(config.queryParameter);

  if (allowedVariants.has(queryVariant)) {
    return {
      enabled: true,
      variant: queryVariant,
      arm: queryVariant === config.control ? "A" : "B",
      source: "qa",
      shouldPersist: false,
    };
  }

  if (!active) {
    return {
      enabled: false,
      variant: config.control,
      arm: "A",
      source: "inactive",
      shouldPersist: false,
    };
  }

  const cookieVariant = readSnatchdExperimentCookie(cookieString, config.cookieName);
  if (allowedVariants.has(cookieVariant)) {
    return {
      enabled: true,
      variant: cookieVariant,
      arm: cookieVariant === config.control ? "A" : "B",
      source: "cookie",
      shouldPersist: false,
    };
  }

  const suppliedRandomValue = Number(options.randomValue);
  const randomValue = Number.isFinite(suppliedRandomValue)
    ? Math.max(0, Math.min(0.999999999999, suppliedRandomValue))
    : getSnatchdExperimentRandomValue();
  const variant = randomValue < config.allocation ? config.control : config.treatment;
  return {
    enabled: true,
    variant,
    arm: variant === config.control ? "A" : "B",
    source: "random",
    shouldPersist: true,
  };
}

function trackSnatchdExperimentEvent(eventName, assignment, details = {}) {
  if (!assignment.enabled || assignment.source === "qa") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    experiment_id: SNATCHD_SUBSCRIPTION_DEFAULT_EXPERIMENT.id,
    experiment_arm: assignment.arm,
    experiment_variant: assignment.variant,
    ...details,
  });
}

function scheduleSnatchdExperimentAttribution(root, assignment) {
  if (!assignment.enabled || assignment.source === "qa") return;
  const config = SNATCHD_SUBSCRIPTION_DEFAULT_EXPERIMENT;
  const sessionKey = `${config.id}:cart-attributed`;
  const attributionValue = `${assignment.arm}_${assignment.variant}`;
  try {
    if (window.sessionStorage.getItem(sessionKey) === attributionValue) return;
  } catch (_error) {
    // Attribution still runs when session storage is unavailable.
  }

  const writeAttribution = async () => {
    const routesRoot = String(window.Shopify?.routes?.root || "/");
    const cartUpdateUrl = `${routesRoot.endsWith("/") ? routesRoot : `${routesRoot}/`}cart/update.js`;
    try {
      const response = await fetch(cartUpdateUrl, {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          attributes: {
            [config.cartAttribute]: attributionValue,
          },
        }),
      });
      if (!response.ok) throw new Error(`Cart attribution failed with ${response.status}`);
      root.dataset.snatchdExperimentAttribution = "recorded";
      try {
        window.sessionStorage.setItem(sessionKey, attributionValue);
      } catch (_error) {
        // A successful cart write is sufficient when storage is unavailable.
      }
    } catch (_error) {
      root.dataset.snatchdExperimentAttribution = "failed";
    }
  };

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(writeAttribution, { timeout: 1200 });
  } else {
    window.setTimeout(writeAttribution, 350);
  }
}

function initSnatchdSubscriptionDefaultExperiment(root) {
  let assignment = window.__snatchdSubscriptionDefaultExperiment;
  if (!assignment) {
    assignment = resolveSnatchdSubscriptionDefaultExperiment();
    window.__snatchdSubscriptionDefaultExperiment = assignment;
    if (assignment.shouldPersist) {
      const config = SNATCHD_SUBSCRIPTION_DEFAULT_EXPERIMENT;
      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `${encodeURIComponent(config.cookieName)}=${encodeURIComponent(assignment.variant)}; Path=/; Max-Age=${config.maxAgeSeconds}; SameSite=Lax${secure}`;
    }
  }

  root.dataset.snatchdSubscriptionExperiment = assignment.enabled ? assignment.variant : "inactive";
  root.dataset.snatchdSubscriptionExperimentArm = assignment.arm;
  root.dataset.snatchdSubscriptionExperimentSource = assignment.source;
  if (assignment.enabled && assignment.source !== "qa" && !window.__snatchdSubscriptionExperimentViewed) {
    window.__snatchdSubscriptionExperimentViewed = true;
    trackSnatchdExperimentEvent("snatchd_experiment_exposure", assignment);
    scheduleSnatchdExperimentAttribution(root, assignment);
  }
  return assignment;
}

function initSnatchdPdp(root) {
  if (root.dataset.snatchdReady === "true") return;
  root.dataset.snatchdReady = "true";

  const gallery = root.querySelector(".gallery");
  const stage = root.querySelector(".gallery-stage");
  const slides = [...root.querySelectorAll(".gallery-slide")];
  const thumbnails = [...root.querySelectorAll(".gallery-thumb")];
  const previousButton = root.querySelector(".gallery-arrow--previous");
  const nextButton = root.querySelector(".gallery-arrow--next");
  const galleryCount = root.querySelector(".gallery-count");
  const zoomButton = root.querySelector(".gallery-zoom");
  const imageDialog = root.querySelector(".image-dialog");
  const dialogImage = imageDialog?.querySelector("img");
  const dialogClose = imageDialog?.querySelector(".image-dialog__close");
  let activeSlide = 0;
  let pointerStartX = null;
  let prefetchTimer = null;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const scrollBehavior = reducedMotion ? "auto" : "smooth";
  const saveData = navigator.connection?.saveData === true;
  const bundleHeading = root.dataset.bundleHeading || "LOW STOCK · SELLING FAST";
  const subscriptionDefaultExperiment = initSnatchdSubscriptionDefaultExperiment(root);

  function initKlaviyoViewedProduct() {
    if (root.dataset.klaviyoViewedProductTracked === "true") return;

    const productId = String(root.dataset.productId || "");
    const productTitle = root.dataset.productTitle || "Snatch'd Lymphatic Drainage Capsules";
    let productImage = "";
    try {
      productImage = new URL(root.dataset.productImage || "", window.location.origin).href;
    } catch (_error) {
      productImage = "";
    }
    const price = Number(root.dataset.productPrice || 0);
    const compareAtPrice = Number(root.dataset.productCompareAtPrice || 0);
    const canonicalPageUrl = `${window.location.origin}${window.location.pathname}`;
    if (!productId || !productImage || !Number.isFinite(price) || price <= 0) return;

    const item = {
      Name: productTitle,
      ProductName: productTitle,
      ProductID: productId,
      SKU: root.dataset.productSku || "",
      Categories: [],
      ImageURL: productImage,
      URL: canonicalPageUrl,
      Brand: root.dataset.productBrand || "Snatch'd",
      Price: price,
      CompareAtPrice: Number.isFinite(compareAtPrice) && compareAtPrice > 0 ? compareAtPrice : price,
      VariantID: String(root.dataset.productVariantId || ""),
      VariantName: root.dataset.productVariantName || "",
      $value: price,
      $value_currency: root.dataset.productCurrency || "USD",
    };

    window._learnq = window._learnq || [];
    window._learnq.push(["track", "Viewed Product", item]);
    window._learnq.push(["trackViewedItem", {
      Title: productTitle,
      ItemId: productId,
      Categories: [],
      ImageUrl: productImage,
      Url: canonicalPageUrl,
      Metadata: {
        Brand: item.Brand,
        Price: price,
        CompareAtPrice: item.CompareAtPrice,
        VariantID: item.VariantID,
        SKU: item.SKU,
      },
    }]);
    root.dataset.klaviyoViewedProductTracked = "true";
  }

  function initSaleCountdown() {
    const countdown = root.querySelector("[data-sale-countdown]");
    if (!countdown) return;
    const announcementLabel = countdown.closest(".announcement")?.querySelector(".announcement__label");
    const duration = (5 * 60 + 52) * 60 * 1000;
    const storageKey = "snatchd_flash_sale_end_v3";
    const now = Date.now();
    let endTime = 0;
    try {
      endTime = Number(window.localStorage.getItem(storageKey));
    } catch (_error) {
      endTime = 0;
    }
    if (!Number.isFinite(endTime) || endTime <= now) {
      endTime = now + duration;
      try {
        window.localStorage.setItem(storageKey, String(endTime));
      } catch (_error) {
        // The timer remains functional for this page view when storage is unavailable.
      }
    }
    const hours = countdown.querySelector("[data-countdown-hours]");
    const minutes = countdown.querySelector("[data-countdown-minutes]");
    const seconds = countdown.querySelector("[data-countdown-seconds]");
    let timer = null;
    const render = () => {
      const remaining = Math.max(0, endTime - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const hourValue = Math.floor(totalSeconds / 3600);
      const minuteValue = Math.floor((totalSeconds % 3600) / 60);
      const secondValue = totalSeconds % 60;
      hours.textContent = String(hourValue).padStart(2, "0");
      minutes.textContent = String(minuteValue).padStart(2, "0");
      seconds.textContent = String(secondValue).padStart(2, "0");
      countdown.setAttribute("aria-label", `${hourValue} hours, ${minuteValue} minutes, ${secondValue} seconds remaining`);
      if (remaining === 0) {
        if (announcementLabel) announcementLabel.textContent = "Limited-Time Offer Available Below";
        countdown.hidden = true;
        countdown.setAttribute("aria-label", "Countdown ended. Limited-time offer available below.");
        if (timer !== null) window.clearInterval(timer);
      }
    };
    render();
    timer = window.setInterval(render, 1000);
  }

  function initShippingEstimate() {
    const dateBadge = root.querySelector("[data-estimated-date]");
    const estimate = root.querySelector("[data-shipping-estimate]");
    if (!dateBadge || !estimate) return;
    const estimatedDate = new Date();
    estimatedDate.setDate(estimatedDate.getDate() + 5);
    dateBadge.textContent = `Est. ${estimatedDate.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}`;

    const slot = root.querySelector("[data-snatchd-kaching]");
    if (!slot) return;
    const placeBeforeCartButton = () => {
      const button = [...slot.querySelectorAll("button, [role='button']")].find((candidate) => /add\s+to\s+cart|add\s+\d+\s+bottles?\s+to\s+cart/i.test(candidate.textContent || ""));
      if (button && button.parentNode && button.previousElementSibling !== estimate) {
        button.parentNode.insertBefore(estimate, button);
      }
    };
    placeBeforeCartButton();
    const observer = new MutationObserver(placeBeforeCartButton);
    observer.observe(slot, { childList: true, subtree: true });
  }

  function initPurchaseCta() {
    const cta = root.querySelector(".snatchd-purchase-cta");
    const currentPrice = cta?.querySelector("[data-snatchd-cta-price]");
    const comparePrice = cta?.querySelector("[data-snatchd-cta-compare]");
    const slot = root.querySelector("[data-snatchd-kaching]");
    const paymentAssurance = () => [...root.querySelectorAll(".checkout-assurance .assurance-label")]
      .find((label) => /one-time payment|auto-refill selected|subscription selected/i.test(label.textContent || ""));
    if (!cta || !currentPrice || !comparePrice || !slot) return;
    let defaultSelectionSettled = false;
    let preferredSelectionFirstSeen = 0;
    let defaultSelectionAttempts = 0;
    let userSelectedOffer = false;
    let userSelectedPurchaseOption = false;
    let purchaseOptionDefaultAttempts = 0;
    const defaultSelectionDeadline = Date.now() + 6000;
    const purchaseOptionDefaultDeadline = Date.now() + 8000;

    const parseJsonScript = (selector) => {
      const records = [...slot.querySelectorAll(selector)].flatMap((script) => {
        if (!script.textContent) return [];
        try {
          return [JSON.parse(script.textContent)];
        } catch (_error) {
          return [];
        }
      });
      if (records.length <= 1 || selector !== ".kaching-bundles-deal-block-settings") {
        return records[0] || null;
      }

      let sessionId = new URLSearchParams(window.location.search)
        .get("preview_kaching_session_id") || "";
      try {
        sessionId = sessionId
          || window.localStorage.getItem("kaching_session_id")
          || window.sessionStorage.getItem("kaching_session_id")
          || "";
      } catch (_error) {
        // Kaching falls back between local and session storage in restricted browsers.
      }
      const sessionPrefix = sessionId.replace(/-/g, "").slice(0, 4);
      const sessionValue = Number.parseInt(sessionPrefix, 16);
      if (!Number.isFinite(sessionValue)) return records[0];
      const bucket = Math.floor(((sessionValue % 256) * 100) / 256);
      return records.find((record) => {
        const range = record?.abTestVariantRange;
        return Array.isArray(range)
          && range.length === 2
          && bucket >= Number(range[0])
          && bucket < Number(range[1]);
      }) || records[0];
    };

    const currency = window.Shopify?.currency?.active
      || document.querySelector('meta[property="og:price:currency"]')?.content
      || "USD";
    const currencyCode = String(currency).toUpperCase();
    const dollarCurrencyPrefixes = {
      AUD: "A",
      CAD: "C",
      HKD: "HK",
      NZD: "NZ",
      SGD: "S",
    };
    const compactDollarPrefix = dollarCurrencyPrefixes[currencyCode] || "";

    const formatMoney = (amountInCents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
          style: "currency",
          currency,
          currencyDisplay: "symbol",
        }).format(amountInCents / 100);
      } catch (_error) {
        return `$${(amountInCents / 100).toFixed(2)} ${currencyCode}`;
      }
    };

    const localizeKachingDollarPrices = () => {
      if (!compactDollarPrefix) return;
      slot.querySelectorAll(
        ".kaching-bundles__bar-price, .kaching-bundles__bar-full-price",
      ).forEach((price) => {
        const priceText = [...price.childNodes]
          .find((node) => node.nodeType === window.Node.TEXT_NODE && /^\s*\$\s*\d/.test(node.nodeValue || ""));
        if (!priceText) return;
        priceText.nodeValue = priceText.nodeValue.replace(
          /^(\s*)\$(?=\s*\d)/,
          (_match, whitespace) => `${whitespace}${compactDollarPrefix}$`,
        );
      });
    };

    const applyPercentageDiscount = (total, type, value) => {
      if (type !== "percentage") return total;
      const percentage = Math.max(0, Math.min(100, Number(value) || 0));
      return Math.round(total * (100 - percentage) / 100);
    };

    const normalizeResourceId = (value) => String(value || "").split("/").pop();

    const getDealVariant = (settings, product, deal, fallbackVariant) => {
      const mapping = settings.defaultVariantsV2?.find((candidate) => candidate.dealBarId === deal?.id);
      const mappedVariantId = normalizeResourceId(mapping?.variantGIDs?.[0]);
      if (!mappedVariantId) return fallbackVariant;
      return product.variants?.find((candidate) => normalizeResourceId(candidate.id) === mappedVariantId)
        || fallbackVariant;
    };

    const subscriptionChoice = () => [...slot.querySelectorAll(
      ".kaching-bundles__subscriptions .kaching-bundles__bar",
    )].find((choice) => /subscribe|auto-refill/i.test(
      choice.querySelector(".kaching-bundles__subscriptions__title")?.textContent || "",
    ));

    const oneTimeChoice = () => [...slot.querySelectorAll(
      ".kaching-bundles__subscriptions .kaching-bundles__bar",
    )].find((choice) => /one-time/i.test(
      choice.querySelector(".kaching-bundles__subscriptions__title")?.textContent || "",
    ));

    const subscriptionIsSelected = () => {
      const choice = subscriptionChoice();
      return Boolean(
        choice
        && (
          choice.getAttribute("aria-checked") === "true"
          || choice.classList.contains("kaching-bundles__bar--selected")
        )
      );
    };

    const purchaseChoiceIsSelected = (choice) => Boolean(
      choice
      && (
        choice.getAttribute("aria-checked") === "true"
        || choice.classList.contains("kaching-bundles__bar--selected")
      )
    );

    const applyPurchaseOptionDefaultExperiment = () => {
      if (!subscriptionDefaultExperiment.enabled || userSelectedPurchaseOption) return false;
      const desiredChoice = subscriptionDefaultExperiment.variant
        === SNATCHD_SUBSCRIPTION_DEFAULT_EXPERIMENT.treatment
        ? subscriptionChoice()
        : oneTimeChoice();
      if (!desiredChoice || purchaseChoiceIsSelected(desiredChoice)) return false;
      if (
        Date.now() >= purchaseOptionDefaultDeadline
        || purchaseOptionDefaultAttempts >= 8
      ) return false;

      const selectionControl = desiredChoice.querySelector(".kaching-bundles__bar-main")
        || desiredChoice.querySelector("label")
        || desiredChoice;
      purchaseOptionDefaultAttempts += 1;
      selectionControl.click();
      return true;
    };

    const getChargedQuantity = (deal) => Math.max(
      1,
      Number(deal?.dealBarType === "bxgy" ? deal.buyQuantity : deal?.quantity) || 1,
    );

    const getAdvertisedPaidQuantity = (deal) => {
      const titleMatch = String(deal?.title || "").match(/\bbuy\s+(\d+)/i);
      return titleMatch
        ? Math.max(1, Number(titleMatch[1]) || 1)
        : getChargedQuantity(deal);
    };

    const getDealPricing = (
      deal,
      variant,
      useSubscriptionPrice = false,
      sellingPlanIdOverride = "",
    ) => {
      if (!deal || !variant || !Number.isFinite(Number(variant.price))) return null;

      const sellingPlanId = normalizeResourceId(sellingPlanIdOverride || deal.sellingPlanGid);
      const sellingPlan = useSubscriptionPrice && sellingPlanId
        ? variant.sellingPlans?.find((candidate) => normalizeResourceId(candidate.id) === sellingPlanId)
        : null;
      const unitPrice = Number(sellingPlan?.price ?? variant.price);
      const compareAtPrice = Number(variant.compareAtPrice) || 0;
      const isBuyGet = deal.dealBarType === "bxgy";
      const paidQuantity = getChargedQuantity(deal);
      const freeQuantity = isBuyGet ? Math.max(0, Number(deal.getQuantity) || 0) : 0;
      const totalQuantity = paidQuantity + freeQuantity;
      const discountType = isBuyGet ? deal.buyDiscountType : deal.discountType;
      const discountValue = isBuyGet ? deal.buyDiscountValue : deal.discountValue;
      const selectedTotal = applyPercentageDiscount(unitPrice * paidQuantity, discountType, discountValue);
      const referenceUnitPrice = !isBuyGet && compareAtPrice > unitPrice ? compareAtPrice : unitPrice;

      return {
        selectedTotal,
        referenceTotal: referenceUnitPrice * totalQuantity,
        totalQuantity,
      };
    };

    const getIntroRenewalPricing = (deal, variant) => {
      const introductoryPlanId = normalizeResourceId(deal?.sellingPlanGid);
      const renewalPlanId = SNATCHD_SUBSCRIPTION_INTRO_RENEWAL_PLANS[introductoryPlanId];
      if (!renewalPlanId) return null;
      return getDealPricing(deal, variant, true, renewalPlanId);
    };

    const renderOfferTotals = (settings, product, fallbackVariant, useSubscriptionPrice) => {
      const bars = [...slot.querySelectorAll(".kaching-bundles__bar[data-deal-bar-id]")];
      settings.dealBars.forEach((deal) => {
        const dealVariant = getDealVariant(settings, product, deal, fallbackVariant);
        const pricing = getDealPricing(deal, dealVariant, useSubscriptionPrice);
        const bar = bars.find((candidate) => candidate.dataset.dealBarId === deal.id);
        const pricingColumn = bar?.querySelector(".kaching-bundles__bar-pricing");
        if (!pricing || !pricingColumn) return;

        const unitLabel = pricingColumn.querySelector(".kaching-bundles__bar-price__unit-label");
        const localizedUnitLabel = "per bottle";
        if (unitLabel && unitLabel.textContent.trim() !== localizedUnitLabel) {
          unitLabel.textContent = localizedUnitLabel;
        }
        if (pricing.totalQuantity <= 1) return;

        let total = pricingColumn.querySelector(".snatchd-bundle-total");
        if (!total) {
          total = document.createElement("div");
          total.className = "snatchd-bundle-total";
          const label = document.createElement("span");
          const value = document.createElement("strong");
          label.textContent = "Total today";
          total.append(label, value);
          pricingColumn.append(total);
        }

        const formattedTotal = formatMoney(pricing.selectedTotal);
        const value = total.querySelector("strong");
        if (value && value.textContent !== formattedTotal) value.textContent = formattedTotal;
        const accessibleLabel = `Total charged today ${formattedTotal}`;
        if (total.getAttribute("aria-label") !== accessibleLabel) {
          total.setAttribute("aria-label", accessibleLabel);
        }
      });
    };

    const getFreeQuantity = (deal) => {
      if (deal.dealBarType === "bxgy") return Math.max(0, Number(deal.getQuantity) || 0);
      const titleMatch = String(deal.title || "").match(/get\s+(\d+)\s+free/i);
      return titleMatch ? Math.max(0, Number(titleMatch[1]) || 0) : 0;
    };

    const getRenewalWeeks = (deal) => {
      const renewalLabel = deal?.highlights?.items
        ?.find((item) => /^renews every\b/i.test(item));
      const match = String(renewalLabel || "").match(/every\s+(\d+)\s+weeks?/i);
      return match ? Math.max(1, Number(match[1]) || 0) : 0;
    };

    const renderSubscriptionChoice = (deal, variant) => {
      const wrapper = slot.querySelector(".kaching-bundles__subscriptions");
      const oneTime = oneTimeChoice();
      const choice = subscriptionChoice();
      const title = choice?.querySelector(".kaching-bundles__subscriptions__title");
      const subtitle = choice?.querySelector(".kaching-bundles__subscriptions__subtitle");
      const oneTimeContent = oneTime?.querySelector(".kaching-bundles__bar-content-left");
      const oneTimePricing = getDealPricing(deal, variant, false);
      const subscriptionPricing = getDealPricing(deal, variant, true);
      if (!wrapper || !oneTime || !choice || !title || !subtitle || !oneTimePricing || !subscriptionPricing) return;
      const renewalPricing = getIntroRenewalPricing(deal, variant);
      const introductoryOffer = Boolean(renewalPricing);

      wrapper.classList.add("snatchd-purchase-options");
      wrapper.classList.toggle(
        "snatchd-purchase-options--split",
        wrapper.classList.contains("kaching-bundles__bars--horizontal"),
      );
      wrapper.classList.toggle("snatchd-purchase-options--intro", introductoryOffer);

      const oneTimeSavings = Math.max(
        0,
        oneTimePricing.referenceTotal - oneTimePricing.selectedTotal,
      );
      const subscriptionSavings = Math.max(
        0,
        subscriptionPricing.referenceTotal - subscriptionPricing.selectedTotal,
      );
      const renewalWeeks = getRenewalWeeks(deal);
      const freeQuantity = getFreeQuantity(deal);
      const paidQuantity = getAdvertisedPaidQuantity(deal);
      oneTime.classList.add("snatchd-purchase-option", "snatchd-purchase-option--one-time");
      choice.classList.add("snatchd-purchase-option", "snatchd-purchase-option--subscription");
      setKachingText(title, "Auto-Refill & Save");
      choice.classList.toggle("snatchd-purchase-option--intro", introductoryOffer);
      oneTime.setAttribute("aria-pressed", String(purchaseChoiceIsSelected(oneTime)));
      choice.setAttribute("aria-pressed", String(purchaseChoiceIsSelected(choice)));

      let valueBadge = title.querySelector(".snatchd-purchase-option__badge");
      if (!valueBadge) {
        valueBadge = document.createElement("span");
        valueBadge.className = "snatchd-purchase-option__badge";
        title.append(valueBadge);
      }
      const introductoryDiscount = SNATCHD_SUBSCRIPTION_INTRO_DISCOUNTS[normalizeResourceId(deal?.sellingPlanGid)];
      const valueBadgeCopy = introductoryOffer && introductoryDiscount
        ? `${introductoryDiscount}% off first delivery` : "SAVE MORE";
      if (valueBadge.textContent !== valueBadgeCopy) valueBadge.textContent = valueBadgeCopy;

      const renderChoicePricing = (purchaseChoice, pricing, context) => {
        const price = purchaseChoice.querySelector(".kaching-bundles__bar-price .ht-money");
        const compare = purchaseChoice.querySelector(".kaching-bundles__bar-full-price .ht-money");
        const pricingColumn = purchaseChoice.querySelector(".kaching-bundles__bar-pricing");
        const selectedPrice = formatMoney(pricing.selectedTotal);
        const referencePrice = formatMoney(pricing.referenceTotal);
        const unitPrice = formatMoney(Math.round(pricing.selectedTotal / pricing.totalQuantity));
        if (price && price.textContent !== selectedPrice) price.textContent = selectedPrice;
        if (compare && compare.textContent !== referencePrice) compare.textContent = referencePrice;
        if (pricingColumn && !introductoryOffer) {
          let priceUnit = pricingColumn.querySelector(".snatchd-purchase-price-unit");
          if (!priceUnit) {
            priceUnit = document.createElement("span");
            priceUnit.className = "snatchd-purchase-price-unit";
            pricingColumn.append(priceUnit);
          }
          const unitCopy = `${unitPrice} per bottle`;
          if (priceUnit.textContent !== unitCopy) priceUnit.textContent = unitCopy;

          let priceContext = pricingColumn.querySelector(".snatchd-purchase-price-context");
          if (!priceContext) {
            priceContext = document.createElement("span");
            priceContext.className = "snatchd-purchase-price-context";
            pricingColumn.append(priceContext);
          }
          if (priceContext.textContent !== context) priceContext.textContent = context;
        } else if (pricingColumn) {
          pricingColumn.querySelector(".snatchd-purchase-price-unit")?.remove();
          let priceContext = pricingColumn.querySelector(".snatchd-purchase-price-context");
          if (!priceContext) {
            priceContext = document.createElement("span");
            priceContext.className = "snatchd-purchase-price-context";
            pricingColumn.append(priceContext);
          }
          if (priceContext.textContent !== context) priceContext.textContent = context;
        }
      };
      renderChoicePricing(oneTime, oneTimePricing, "today");
      renderChoicePricing(
        choice,
        subscriptionPricing,
        introductoryOffer ? "today" : "per delivery",
      );

      if (oneTimeContent) {
        let oneTimeSubtitle = oneTimeContent.querySelector(".snatchd-one-time-copy");
        if (!oneTimeSubtitle) {
          oneTimeSubtitle = document.createElement("div");
          oneTimeSubtitle.className = "kaching-bundles__subscriptions__subtitle snatchd-one-time-copy";
          oneTimeContent.append(oneTimeSubtitle);
        }
        const oneTimeValueCopy = oneTimeSavings
          ? `Save ${formatMoney(oneTimeSavings)} today`
          : "Pay once";
        const oneTimeFlexibilityCopy = "Pay once · no subscription";
        const oneTimeCopyKey = `${oneTimeValueCopy}|${oneTimeFlexibilityCopy}`;
        if (oneTimeSubtitle.dataset.snatchdOneTimeCopy !== oneTimeCopyKey) {
          const valueLine = document.createElement("span");
          valueLine.className = "snatchd-one-time-copy__value";
          valueLine.textContent = oneTimeValueCopy;
          const flexibilityLine = document.createElement("span");
          flexibilityLine.className = "snatchd-one-time-copy__flexibility";
          flexibilityLine.textContent = oneTimeFlexibilityCopy;
          oneTimeSubtitle.replaceChildren(valueLine, flexibilityLine);
          oneTimeSubtitle.dataset.snatchdOneTimeCopy = oneTimeCopyKey;
        }
        oneTimeSubtitle.setAttribute("aria-label", `${oneTimeValueCopy}. ${oneTimeFlexibilityCopy}`);
      }

      const defaultValueCopy = subscriptionSavings
        ? `Save ${formatMoney(subscriptionSavings)} each shipment`
        : "Subscriber savings each shipment";
      const paidBottleLabel = `${paidQuantity} ${paidQuantity === 1 ? "bottle" : "bottles"}`;
      const refillValueCopy = freeQuantity
        ? `${freeQuantity} FREE ${freeQuantity === 1 ? "bottle" : "bottles"} every refill`
        : `${paidBottleLabel} every refill`;
      const defaultCadenceCopy = renewalWeeks ? `Every ${renewalWeeks} weeks` : "";
      const flexibilityCopy = "Skip, pause, or cancel anytime";
      const valueCopy = introductoryOffer ? "" : defaultValueCopy;
      const displayedRefillCopy = introductoryOffer
        ? (
          freeQuantity === 1
            ? "1 FREE bottle every refill"
            : freeQuantity > 1
              ? `${freeQuantity} FREE bottles every refill`
              : "Automatic refills"
        )
        : refillValueCopy;
      const renewalCopy = introductoryOffer && renewalPricing
        ? `Then ${formatMoney(renewalPricing.selectedTotal)} ${renewalWeeks ? `every ${renewalWeeks} weeks` : "per refill"}`
        : "";
      const termsCopy = introductoryOffer
        ? flexibilityCopy
        : [defaultCadenceCopy, flexibilityCopy].filter(Boolean).join(" · ");
      const subscriptionPricingColumn = choice.querySelector(".kaching-bundles__bar-pricing");
      let renewalPrice = subscriptionPricingColumn?.querySelector(".snatchd-purchase-price-renewal");
      if (renewalCopy && subscriptionPricingColumn) {
        if (!renewalPrice) {
          renewalPrice = document.createElement("span");
          renewalPrice.className = "snatchd-purchase-price-renewal";
          subscriptionPricingColumn.append(renewalPrice);
        }
        if (renewalPrice.textContent !== renewalCopy) renewalPrice.textContent = renewalCopy;
      } else {
        renewalPrice?.remove();
      }
      const copyKey = `${valueCopy}|${displayedRefillCopy}|${renewalCopy}|${termsCopy}`;
      if (subtitle.dataset.snatchdSubscriptionCopy !== copyKey) {
        subtitle.replaceChildren();
        if (valueCopy) {
          const valueLine = document.createElement("span");
          valueLine.className = "snatchd-subscription-copy__value";
          valueLine.textContent = valueCopy;
          subtitle.append(valueLine);
        }
        if (displayedRefillCopy) {
          const refillLine = document.createElement("span");
          refillLine.className = "snatchd-subscription-copy__refill";
          refillLine.textContent = displayedRefillCopy;
          subtitle.append(refillLine);
        }
        if (termsCopy) {
          const termsLine = document.createElement("span");
          termsLine.className = "snatchd-subscription-copy__terms";
          termsLine.textContent = termsCopy;
          subtitle.append(termsLine);
        }
        subtitle.dataset.snatchdSubscriptionCopy = copyKey;
      }
      subtitle.setAttribute(
        "aria-label",
        [valueCopy, displayedRefillCopy, renewalCopy, termsCopy].filter(Boolean).join(". "),
      );
    };

    const renderSubscriptionOnlyHighlights = (useSubscriptionPrice, deal) => {
      slot.classList.toggle("snatchd-subscription-selected", useSubscriptionPrice);
      slot.querySelectorAll(".kaching-bundles__highlights__item").forEach((item) => {
        const textElement = item.querySelector(".kaching-bundles__highlights__text");
        const text = textElement?.textContent || "";
        const subscriptionOnly = item.classList.contains("snatchd-subscription-only")
          || /\bevery delivery\b|\brenews every\b|\bevery \d+ weeks?\b/i.test(text);
        const redundantGuarantee = /money-back guarantee/i.test(text);
        item.classList.toggle("snatchd-subscription-only", subscriptionOnly);
        item.classList.remove("snatchd-subscription-duplicate");
        item.classList.toggle("snatchd-redundant-guarantee", redundantGuarantee);
        item.hidden = redundantGuarantee || subscriptionOnly;
      });
      slot.querySelectorAll(".kaching-bundles__highlights").forEach((highlights) => {
        const items = [...highlights.querySelectorAll(".kaching-bundles__highlights__item")];
        const hasVisibleHighlights = items.some((item) => !item.hidden);
        highlights.hidden = !hasVisibleHighlights;
      });
    };

    const renderOfferDetails = (settings, product, fallbackVariant) => {
      const bottleImage = slot.dataset.snatchdBottleImage;
      const bars = [...slot.querySelectorAll(".kaching-bundles__bar[data-deal-bar-id]")];
      if (!bottleImage || !bars.length) return;

      settings.dealBars.forEach((deal) => {
        const dealVariant = getDealVariant(settings, product, deal, fallbackVariant);
        const pricing = getDealPricing(deal, dealVariant);
        const bar = bars.find((candidate) => candidate.dataset.dealBarId === deal.id);
        const main = bar?.querySelector(".kaching-bundles__bar-main");
        const radio = main?.querySelector(".kaching-bundles__bar-radio");
        const secondLine = main?.querySelector(".kaching-bundles__bar-second-line");
        const subtitle = secondLine?.querySelector(".kaching-bundles__bar-subtitle");
        if (!pricing || !bar || !main || !secondLine || !subtitle) return;

        let visual = main.querySelector(".snatchd-offer-visual");
        if (!visual) {
          visual = document.createElement("span");
          visual.className = "snatchd-offer-visual";
          visual.setAttribute("aria-hidden", "true");
          const image = document.createElement("img");
          image.alt = "";
          image.width = 48;
          image.height = 48;
          image.loading = "lazy";
          const count = document.createElement("b");
          count.className = "snatchd-offer-visual__count";
          visual.append(image, count);
          if (radio) radio.after(visual);
          else main.prepend(visual);
        }

        const image = visual.querySelector("img");
        const count = visual.querySelector(".snatchd-offer-visual__count");
        if (image && image.getAttribute("src") !== bottleImage) image.src = bottleImage;
        const quantityLabel = `×${pricing.totalQuantity}`;
        if (count && count.textContent !== quantityLabel) count.textContent = quantityLabel;

        const offerMeta = `${pricing.totalQuantity}-Month Supply`;
        if (subtitle.textContent !== offerMeta) subtitle.textContent = offerMeta;

        secondLine.querySelector(".snatchd-offer-free")?.remove();
      });

      slot.classList.add("snatchd-kaching-enhanced");
    };

    const mostPopularDeal = (settings) => {
      const mostPopularBadge = settings.badges?.find((badge) => /most popular/i.test(badge.text || ""));
      const badgeDealId = mostPopularBadge?.attachedTo?.dealBarId;
      return settings.dealBars.find((deal) => deal.id === badgeDealId)
        || settings.dealBars.find((deal) => deal.dealBarType === "bxgy" && Number(deal.getQuantity) === 1)
        || null;
    };

    const preferMostPopularForNewSession = (settings) => {
      if (defaultSelectionSettled || userSelectedOffer) return;
      const preferredDeal = mostPopularDeal(settings);
      if (!preferredDeal) {
        defaultSelectionSettled = true;
        return;
      }

      const selectedBar = slot.querySelector(".kaching-bundles__bar--selected[data-deal-bar-id]");
      if (selectedBar?.dataset.dealBarId === preferredDeal.id) {
        if (!preferredSelectionFirstSeen) preferredSelectionFirstSeen = Date.now();
        if (Date.now() - preferredSelectionFirstSeen >= 1200) defaultSelectionSettled = true;
        return;
      }

      preferredSelectionFirstSeen = 0;
      if (Date.now() >= defaultSelectionDeadline || defaultSelectionAttempts >= 6) {
        defaultSelectionSettled = true;
        return;
      }

      const preferredBar = slot.querySelector(`.kaching-bundles__bar[data-deal-bar-id="${preferredDeal.id}"]`);
      const selectionControl = preferredBar?.querySelector(".kaching-bundles__bar-main")
        || preferredBar?.querySelector("label");
      if (!selectionControl) return;
      defaultSelectionAttempts += 1;
      selectionControl.click();
    };

    const setKachingText = (element, text) => {
      if (!element) return;
      const textNode = [...element.childNodes].find((node) => node.nodeType === window.Node.TEXT_NODE);
      if (textNode) {
        if (textNode.nodeValue !== text) textNode.nodeValue = text;
        return;
      }
      element.prepend(document.createTextNode(text));
    };

    const renderOfferMerchandising = () => {
      setKachingText(slot.querySelector(".kaching-bundles__block-title"), bundleHeading);
      slot.querySelectorAll(".kaching-bundles__bar-most-popular__content").forEach((badge) => {
        const currentText = badge.textContent || "";
        const bestValue = /best value/i.test(currentText);
        badge.closest(".kaching-bundles__bar-most-popular")
          ?.classList.toggle("snatchd-badge--best-value", bestValue);
        if (/most popular/i.test(currentText)) setKachingText(badge, "MOST POPULAR");
        if (bestValue) setKachingText(badge, "BEST VALUE");
      });
      slot.querySelectorAll(".kaching-bundles__bar-label").forEach((label) => {
        label.classList.toggle("snatchd-empty-badge", !(label.textContent || "").trim());
      });
    };

    const renderBundlePrice = () => {
      renderOfferMerchandising();
      const product = parseJsonScript(".kaching-bundles-product");
      const settings = parseJsonScript(".kaching-bundles-deal-block-settings");
      if (!product || !settings?.dealBars?.length) return;

      const fallbackVariant = product.variants?.find((candidate) => candidate.id === product.selectedVariantId)
        || product.variants?.[0];
      if (!fallbackVariant) return;

      preferMostPopularForNewSession(settings);
      applyPurchaseOptionDefaultExperiment();
      const useSubscriptionPrice = subscriptionIsSelected();
      renderOfferDetails(settings, product, fallbackVariant);
      renderOfferTotals(settings, product, fallbackVariant, useSubscriptionPrice);

      const selectedBar = slot.querySelector(".kaching-bundles__bar--selected[data-deal-bar-id]");
      const selectedDealId = selectedBar?.dataset.dealBarId || settings.preselectedDealBarId;
      const selectedDeal = settings.dealBars.find((candidate) => candidate.id === selectedDealId);
      const selectedVariant = getDealVariant(settings, product, selectedDeal, fallbackVariant);
      const pricing = getDealPricing(selectedDeal, selectedVariant, useSubscriptionPrice);
      if (!pricing) return;

      renderSubscriptionChoice(selectedDeal, selectedVariant);
      renderSubscriptionOnlyHighlights(useSubscriptionPrice, selectedDeal);
      localizeKachingDollarPrices();

      currentPrice.textContent = formatMoney(pricing.selectedTotal);
      comparePrice.textContent = formatMoney(pricing.referenceTotal);
      comparePrice.hidden = pricing.referenceTotal <= pricing.selectedTotal;
      const currentPaymentAssurance = paymentAssurance();
      if (currentPaymentAssurance) {
        const renewalWeeks = getRenewalWeeks(selectedDeal);
        const cadenceCopy = renewalWeeks ? `Every ${renewalWeeks} Weeks` : "Recurring Delivery";
        currentPaymentAssurance.textContent = useSubscriptionPrice
          ? `Auto-Refill Selected · ${cadenceCopy}`
          : "One-Time Payment · No Subscription";
      }
    };

    let renderFrame = null;
    const scheduleRender = () => {
      if (renderFrame !== null) return;
      renderFrame = window.requestAnimationFrame(() => {
        renderFrame = null;
        renderBundlePrice();
      });
    };

    renderBundlePrice();
    new MutationObserver(scheduleRender).observe(slot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "data-deal-bar-id", "aria-checked"],
    });
    const recordOfferChoice = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (event.isTrusted && target?.closest(".kaching-bundles__bar[data-deal-bar-id]")) {
        userSelectedOffer = true;
      }
      const purchaseChoice = target?.closest(
        ".kaching-bundles__subscriptions .kaching-bundles__bar",
      );
      const isPurchaseIntent = event.type === "pointerdown"
        || (event.type === "keydown" && ["Enter", " "].includes(event.key));
      if (!isPurchaseIntent || !purchaseChoice) return;
      userSelectedPurchaseOption = true;
      const selectedOption = purchaseChoice === subscriptionChoice() ? "auto_refill" : "one_time";
      root.dataset.snatchdPurchaseOptionChoice = selectedOption;
      trackSnatchdExperimentEvent(
        "snatchd_purchase_option_selected",
        subscriptionDefaultExperiment,
        { purchase_option: selectedOption },
      );
    };
    const preserveSelectedOffer = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const selectedOffer = target?.closest(
        ".kaching-bundles__bar--selected[data-deal-bar-id]",
      );
      if (!selectedOffer) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      scheduleRender();
    };
    slot.addEventListener("pointerdown", recordOfferChoice, true);
    slot.addEventListener("keydown", recordOfferChoice, true);
    slot.addEventListener("click", preserveSelectedOffer, true);
    slot.addEventListener("click", () => {
      scheduleRender();
      window.setTimeout(scheduleRender, 120);
    });
    [350, 900, 1700, 3000, 5000].forEach((delay) => window.setTimeout(scheduleRender, delay));
  }

  const geoAbbreviations = { US: "USA", GB: "UK", AE: "UAE" };
  const geoNameFallbacks = {
    US: "United States",
    CA: "Canada",
    GB: "United Kingdom",
    IE: "Ireland",
    AU: "Australia",
    NZ: "New Zealand",
  };

  function getRegionName(countryCode) {
    try {
      const displayNames = new Intl.DisplayNames([navigator.language || "en"], { type: "region" });
      return displayNames.of(countryCode) || geoNameFallbacks[countryCode] || countryCode;
    } catch (_error) {
      return geoNameFallbacks[countryCode] || countryCode;
    }
  }

  function emojiFlag(countryCode) {
    return String.fromCodePoint(...countryCode.toUpperCase().split("").map((character) => 127397 + character.charCodeAt()));
  }

  async function detectCountry(proof) {
    if (window.__geoCountry) return window.__geoCountry;
    let countryCode = proof.dataset.countryIso || "";

    if (!countryCode && window.Shopify) {
      try {
        countryCode = window.Shopify.country || window.Shopify.locale?.country || "";
      } catch (_error) {
        countryCode = "";
      }
    }

    if (!countryCode) {
      try {
        const response = await fetch("https://api.country.is/", { cache: "reload" });
        if (response.ok) {
          const data = await response.json();
          countryCode = data?.country || "";
        }
      } catch (_error) {
        countryCode = "";
      }
    }

    window.__geoCountry = String(countryCode || "US").toUpperCase();
    return window.__geoCountry;
  }

  function applyCountryProof(proof, countryCode) {
    const flag = proof.querySelector(".geo-proof__flag");
    const country = proof.querySelector("[data-geo-country]");
    if (!flag || !country || proof.dataset.geoApplied === countryCode) return;

    const fullCountryName = getRegionName(countryCode);
    country.textContent = geoAbbreviations[countryCode] || fullCountryName;
    flag.alt = `${fullCountryName} flag`;
    flag.src = `https://flagcdn.com/${countryCode.toLowerCase()}.svg`;
    flag.onerror = function replaceMissingFlag() {
      const fallback = document.createElement("span");
      fallback.className = "geo-proof__flag-fallback";
      fallback.setAttribute("aria-hidden", "true");
      fallback.textContent = emojiFlag(countryCode);
      this.replaceWith(fallback);
    };
    proof.dataset.geoApplied = countryCode;
  }

  function initGeoProof() {
    const proof = root.querySelector(".geo-proof");
    if (!proof) return;
    detectCountry(proof).then((countryCode) => applyCountryProof(proof, countryCode));
  }

  function initProofSurvey() {
    const survey = root.querySelector("[data-proof-survey]");
    if (!survey) return;
    if (reducedMotion || !("IntersectionObserver" in window)) {
      survey.classList.add("is-visible");
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      survey.classList.add("is-visible");
      observer.disconnect();
    }, { threshold: 0.24 });
    observer.observe(survey);
  }

  function hydrateSlide(index, selected = false) {
    const slide = slides[index];
    if (!slide || slide.getAttribute("src") || !slide.dataset.src) return;
    slide.fetchPriority = selected ? "high" : "low";
    slide.src = slide.dataset.src;
  }

  function queueNextSlide(index) {
    if (saveData) return;
    window.clearTimeout(prefetchTimer);
    prefetchTimer = window.setTimeout(() => hydrateSlide((index + 1) % slides.length), 1200);
  }

  function centerThumbnail(thumbnail) {
    const thumbnailStrip = thumbnail?.parentElement;
    if (!thumbnailStrip) return;
    const targetLeft = thumbnail.offsetLeft - (thumbnailStrip.clientWidth - thumbnail.offsetWidth) / 2;
    thumbnailStrip.scrollTo({ left: Math.max(0, targetLeft), behavior: scrollBehavior });
  }

  function showSlide(index, moveThumbnail = false) {
    activeSlide = (index + slides.length) % slides.length;
    hydrateSlide(activeSlide, true);
    slides.forEach((slide, slideIndex) => {
      const selected = slideIndex === activeSlide;
      slide.classList.toggle("is-active", selected);
      slide.setAttribute("aria-hidden", String(!selected));
    });
    thumbnails.forEach((thumbnail, thumbnailIndex) => {
      const selected = thumbnailIndex === activeSlide;
      thumbnail.classList.toggle("is-active", selected);
      thumbnail.setAttribute("aria-selected", String(selected));
      if (selected && moveThumbnail) {
        centerThumbnail(thumbnail);
      }
    });
    galleryCount.textContent = `${activeSlide + 1} / ${slides.length}`;
    queueNextSlide(activeSlide);
  }

  function openImageDialog(slide, label = "Enlarged product image") {
    if (!slide || !imageDialog || !dialogImage) return;
    const slideIndex = slides.indexOf(slide);
    if (slideIndex >= 0) hydrateSlide(slideIndex, true);
    dialogImage.src = slide.currentSrc || slide.src || slide.dataset.src || "";
    dialogImage.alt = slide.alt;
    imageDialog.setAttribute("aria-label", label);
    imageDialog.showModal();
  }

  thumbnails.forEach((thumbnail) => thumbnail.addEventListener("click", () => showSlide(Number(thumbnail.dataset.target))));
  root.querySelectorAll("[data-show-supplement-facts]").forEach((button) => {
    button.addEventListener("click", () => {
      const supplementFacts = slides.find((slide) => slide.dataset.slide === "5");
      openImageDialog(supplementFacts, "Snatch'd Supplement Facts");
    });
  });
  previousButton?.addEventListener("click", () => showSlide(activeSlide - 1, true));
  nextButton?.addEventListener("click", () => showSlide(activeSlide + 1, true));

  gallery?.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      showSlide(activeSlide - 1, true);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      showSlide(activeSlide + 1, true);
    }
  });

  stage?.addEventListener("pointerdown", (event) => {
    pointerStartX = event.clientX;
  });
  stage?.addEventListener("pointerup", (event) => {
    if (pointerStartX === null) return;
    const distance = event.clientX - pointerStartX;
    pointerStartX = null;
    if (Math.abs(distance) >= 45) showSlide(activeSlide + (distance < 0 ? 1 : -1), true);
  });
  stage?.addEventListener("pointercancel", () => {
    pointerStartX = null;
  });

  zoomButton?.addEventListener("click", () => {
    openImageDialog(slides[activeSlide]);
  });
  dialogClose?.addEventListener("click", () => imageDialog.close());
  imageDialog?.addEventListener("click", (event) => {
    if (event.target === imageDialog) imageDialog.close();
  });

  root.querySelectorAll(".product-accordions, .benefit-list, .faq-list").forEach((group) => {
    const details = [...group.querySelectorAll("details")];
    details.forEach((item) => {
      item.addEventListener("toggle", () => {
        if (!item.open) return;
        details.forEach((sibling) => {
          if (sibling !== item) sibling.open = false;
        });
      });
    });
  });

  root.querySelectorAll("[data-carousel]").forEach((carousel) => {
    const track = carousel.querySelector("[data-carousel-track]");
    const cards = track ? [...track.children] : [];
    const dots = [...carousel.querySelectorAll("[data-carousel-index]")];
    const previous = carousel.querySelector("[data-carousel-previous]");
    const next = carousel.querySelector("[data-carousel-next]");
    if (!track || cards.length === 0 || !previous || !next) return;

    let activeIndex = 0;
    let scrollFrame = null;
    let programmaticScroll = false;
    let scrollSettleTimer = null;

    const updateControls = (index) => {
      activeIndex = Math.max(0, Math.min(index, cards.length - 1));
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle("is-active", dotIndex === activeIndex);
        dot.setAttribute("aria-current", dotIndex === activeIndex ? "true" : "false");
      });
      previous.disabled = activeIndex === 0;
      next.disabled = activeIndex === cards.length - 1;
    };

    const moveTo = (index) => {
      const targetIndex = Math.max(0, Math.min(index, cards.length - 1));
      const target = cards[targetIndex];
      programmaticScroll = true;
      window.clearTimeout(scrollSettleTimer);
      track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: scrollBehavior });
      updateControls(targetIndex);
      scrollSettleTimer = window.setTimeout(() => {
        programmaticScroll = false;
      }, reducedMotion ? 0 : 500);
    };

    const releaseProgrammaticScroll = () => {
      programmaticScroll = false;
      window.clearTimeout(scrollSettleTimer);
    };

    previous.addEventListener("click", () => moveTo(activeIndex - 1));
    next.addEventListener("click", () => moveTo(activeIndex + 1));
    dots.forEach((dot) => dot.addEventListener("click", () => moveTo(Number(dot.dataset.carouselIndex))));
    track.addEventListener("pointerdown", releaseProgrammaticScroll, { passive: true });
    track.addEventListener("scroll", () => {
      if (programmaticScroll) return;
      if (scrollFrame !== null) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = null;
        const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const progress = maximumScroll > 0 ? track.scrollLeft / maximumScroll : 0;
        updateControls(Math.round(progress * (cards.length - 1)));
      });
    }, { passive: true });

    updateControls(0);
  });

  initPurchaseCta();
  initSaleCountdown();
  initShippingEstimate();
  initGeoProof();
  initProofSurvey();
  initKlaviyoViewedProduct();
  showSlide(0);
}

function initAllSnatchdPdps(scope = document) {
  scope.querySelectorAll(".sn-pdp").forEach(initSnatchdPdp);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initAllSnatchdPdps());
} else {
  initAllSnatchdPdps();
}

document.addEventListener("shopify:section:load", (event) => initAllSnatchdPdps(event.target));
